import { Typography } from '@mui/material'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import DatePicker from 'react-multi-date-picker'
import dayjs from '../../../../utils/dayjsConfig'
import 'dayjs/locale/es'
import { DATE_PICKER_SETTINGS } from '../../../../constants/constant'
import { SETTLEMENT_DETAILS_DEPOSITS_DATE_PLACEHOLDER } from '../../../../constants/settlementDetailsConstants'
import {
  UI_COLOR_BORDER_CARD,
  UI_COLOR_DANGER,
  UI_COLOR_PRIMARY,
  UI_COLOR_TEXT_HEADING,
  UI_COLOR_TEXT_SUBTITLE,
  UI_COLOR_WHITE
} from '../../../../constants/uiColors'
import {
  DEPOSIT_DIALOG_DATE_LOCALE_TEXT,
  DEPOSIT_DIALOG_ERROR_TEXT_SX,
  DEPOSIT_DIALOG_INPUT_HEIGHT,
  EDITABLE_INPUT_HEIGHT
} from '../../../helpers/settlementDetailsTabConstants'
import {
  buildDateObjectFromNormalizedValue,
  formatSingleDateLabel,
  normalizeDateInputValue
} from '../../../helpers/settlementDetailsTabHelpers'

export default function SingleDateDepositPicker({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText = '',
  dialog = false,
  ariaLabel
}) {
  const normalizedValue = normalizeDateInputValue(value)
  const handleChange = (date) => onChange(date ? date.format('YYYY-MM-DD') : '')

  if (dialog) {
    const muiPickerValue = normalizedValue ? dayjs(normalizedValue, 'YYYY-MM-DD', true) : null
    return (
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="es"
        localeText={DEPOSIT_DIALOG_DATE_LOCALE_TEXT}
      >
        <MuiDatePicker
          value={muiPickerValue}
          onChange={handleChange}
          disabled={disabled}
          format="DD/MM/YYYY"
          maxDate={dayjs()}
          sx={{ width: '100%', height: DEPOSIT_DIALOG_INPUT_HEIGHT }}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              error,
              helperText,
              inputProps: { 'aria-label': ariaLabel },
              sx: {
                width: '100%',
                height: DEPOSIT_DIALOG_INPUT_HEIGHT,
                '& .MuiPickersOutlinedInput-root': {
                  height: '100%',
                  minHeight: DEPOSIT_DIALOG_INPUT_HEIGHT,
                  maxHeight: DEPOSIT_DIALOG_INPUT_HEIGHT,
                  boxSizing: 'border-box',
                  borderRadius: '4px',
                  color: UI_COLOR_TEXT_HEADING,
                  backgroundColor: UI_COLOR_WHITE,
                  '& .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: UI_COLOR_BORDER_CARD
                  },
                  '&:hover .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: UI_COLOR_TEXT_SUBTITLE
                  },
                  '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: UI_COLOR_PRIMARY
                  },
                  '&.Mui-error .MuiPickersOutlinedInput-notchedOutline, &.Mui-error:hover .MuiPickersOutlinedInput-notchedOutline, &.Mui-error.Mui-focused .MuiPickersOutlinedInput-notchedOutline':
                    { borderColor: UI_COLOR_DANGER }
                },
                '& .MuiPickersOutlinedInput-sectionsContainer': {
                  height: '100%',
                  py: '0 !important',
                  alignItems: 'center',
                  boxSizing: 'border-box'
                },
                '& .MuiPickersInputBase-sectionContent': {
                  fontSize: 14,
                  color: UI_COLOR_TEXT_HEADING,
                  WebkitTextFillColor: UI_COLOR_TEXT_HEADING
                },
                '& .MuiIconButton-root': {
                  width: 32,
                  height: 32,
                  p: 0.5,
                  color: UI_COLOR_TEXT_SUBTITLE
                },
                '& .MuiFormHelperText-root, & .MuiFormHelperText-root.Mui-error': {
                  ...DEPOSIT_DIALOG_ERROR_TEXT_SX,
                  mx: 0,
                  mt: 0.25
                }
              }
            },
            popper: { sx: (theme) => ({ zIndex: theme.zIndex.modal + 1 }) }
          }}
        />
      </LocalizationProvider>
    )
  }

  const pickerValue = normalizedValue ? buildDateObjectFromNormalizedValue(normalizedValue) : null
  const borderColor = error ? UI_COLOR_DANGER : UI_COLOR_PRIMARY
  return (
    <DatePicker
      value={pickerValue}
      onChange={handleChange}
      multiple={false}
      range={false}
      disabled={disabled}
      months={DATE_PICKER_SETTINGS.months}
      weekDays={DATE_PICKER_SETTINGS.weekDays}
      weekStartDayIndex={DATE_PICKER_SETTINGS.weekStartDayIndex}
      format="DD/MM/YYYY"
      maxDate={DATE_PICKER_SETTINGS.maxDate()}
      highlightToday={false}
      render={(_, openCalendar) => (
        <div style={{ minWidth: 100, width: '100%' }}>
          <div
            className="react-filter-box"
            style={{
              width: '100%',
              height: dialog ? 40 : EDITABLE_INPUT_HEIGHT,
              borderColor,
              marginTop: 0,
              paddingLeft: 0,
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              opacity: disabled ? 0.6 : 1,
              pointerEvents: disabled ? 'none' : 'auto'
            }}
          >
            <div
              onClick={disabled ? undefined : openCalendar}
              role="button"
              aria-label={ariaLabel}
              tabIndex={disabled ? -1 : 0}
              onKeyDown={(event) => {
                if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault()
                  openCalendar()
                }
              }}
              className="react-filter-box-clickable-zone"
              style={{ padding: '0 12px', height: '100%', fontSize: dialog ? 14 : 12 }}
            >
              <div
                className="react-filter-titles"
                style={{
                  color: dialog ? UI_COLOR_TEXT_HEADING : UI_COLOR_PRIMARY,
                  width: '100%',
                  gap: dialog ? 8 : 0
                }}
              >
                {dialog ? (
                  <SearchOutlinedIcon sx={{ color: UI_COLOR_TEXT_SUBTITLE, fontSize: 20 }} />
                ) : null}
                <div
                  className="filter-title-overflow"
                  style={{ color: dialog ? UI_COLOR_TEXT_HEADING : UI_COLOR_PRIMARY, flex: 1 }}
                >
                  {normalizedValue
                    ? formatSingleDateLabel(normalizedValue)
                    : SETTLEMENT_DETAILS_DEPOSITS_DATE_PLACEHOLDER}
                </div>
                <CalendarTodayOutlinedIcon
                  sx={{ color: dialog ? UI_COLOR_TEXT_SUBTITLE : UI_COLOR_PRIMARY, fontSize: 18 }}
                />
              </div>
            </div>
          </div>
          {helperText ? (
            <Typography sx={{ ...DEPOSIT_DIALOG_ERROR_TEXT_SX, mt: 0.25 }}>{helperText}</Typography>
          ) : null}
        </div>
      )}
    />
  )
}
